import { useState, useCallback, useMemo } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin/desktop';
import { 
  ITaskFormValues, 
  defaultTaskFormValues, 
  TaskStatusType, 
  TaskPriorityEnum, 
  TaskCategoryType,
  ITaskAssignee,
  ISubtaskItem,
} from '@/types/admin/tasks';

interface FormErrors {
  title?: string;
  taskStatus?: string;
  priority?: string;
  category?: string;
  description?: string;
  location?: string;
  dueDate?: string;
  assignees?: string;
  subtasks?: string;
  [key: string]: string | undefined;
}

export const useCreateTaskForm = () => {
  const [formData, setFormData] = useState<ITaskFormValues>(defaultTaskFormValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [initialFormData, setInitialFormData] = useState<ITaskFormValues>(defaultTaskFormValues);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);

  // Field change handler with validation
  const handleFieldChange = useCallback((field: keyof ITaskFormValues, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasInteracted(true);
    
    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Validation function
  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.title?.trim()) {
      newErrors.title = 'Task name is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Task name must be at least 3 characters';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Task name must be less than 100 characters';
    }

    if (!formData.taskStatus) {
      newErrors.taskStatus = 'Task status is required';
    }

    if (!formData.priority) {
      newErrors.priority = 'Task priority is required';
    }

    if (!formData.category) {
      newErrors.category = 'Task category is required';
    }

    // Optional field validations
    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.location && formData.location.length > 100) {
      newErrors.location = 'Location must be less than 100 characters';
    }

    // Due date validation
    if (formData.dueDate) {
      const dueDate = new Date(formData.dueDate);
      const now = new Date();
      if (dueDate < now) {
        newErrors.dueDate = 'Due date cannot be in the past';
      }
    }

    // Subtasks validation
    if (formData.subtasks && formData.subtasks.length > 20) {
      newErrors.subtasks = 'Maximum 20 subtasks allowed';
    }

    // Only set errors if user has interacted with the form
    if (hasInteracted) {
      setErrors(newErrors);
    }
    return newErrors;
  }, [formData, hasInteracted]);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  // Show discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.discardTitle,
      DESKTOP_CONFIRM_COPY.discardMessage,
      DESKTOP_CONFIRM_COPY.discardConfirmText,
      DESKTOP_CONFIRM_COPY.discardCancelText
    );
    
    if (confirmed) {
      onConfirm();
    }
  }, []);

  // Assignee management
  const addAssignee = useCallback((assignee: ITaskAssignee) => {
    setFormData(prev => ({
      ...prev,
      assignees: [...prev.assignees, assignee],
    }));
  }, []);

  const removeAssignee = useCallback((assigneeId: number) => {
    setFormData(prev => ({
      ...prev,
      assignees: prev.assignees.filter(a => a.id !== assigneeId),
    }));
  }, []);

  const toggleAssignee = useCallback((assignee: ITaskAssignee) => {
    const isSelected = formData.assignees.some(a => a.id === assignee.id);
    if (isSelected) {
      removeAssignee(assignee.id!);
    } else {
      addAssignee(assignee);
    }
  }, [formData.assignees, addAssignee, removeAssignee]);

  // Subtask management
  const addSubtask = useCallback((title: string) => {
    if (!title.trim()) return;

    const newSubtask: ISubtaskItem = {
      id: Date.now(), // Temporary ID for new subtasks
      title: title.trim(),
      sortIndex: formData.subtasks.length,
      assignee: '',
      createdAt: new Date().toISOString(),
    };

    setFormData(prev => ({
      ...prev,
      subtasks: [...prev.subtasks, newSubtask],
    }));
  }, [formData.subtasks.length]);

  const updateSubtask = useCallback((index: number, updates: Partial<ISubtaskItem>) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map((subtask, i) => 
        i === index ? { ...subtask, ...updates } : subtask
      ),
    }));
  }, []);

  const removeSubtask = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index),
    }));
  }, []);

  const reorderSubtasks = useCallback((fromIndex: number, toIndex: number) => {
    setFormData(prev => {
      const newSubtasks = [...prev.subtasks];
      const [movedItem] = newSubtasks.splice(fromIndex, 1);
      newSubtasks.splice(toIndex, 0, movedItem);
      
      // Update sort indices
      return {
        ...prev,
        subtasks: newSubtasks.map((subtask, index) => ({
          ...subtask,
          sortIndex: index,
        })),
      };
    });
  }, []);

  // Form validation state
  const isFormValid = useMemo(() => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0;
  }, [validateForm]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(defaultTaskFormValues);
    setErrors({});
    setInitialFormData(defaultTaskFormValues);
  }, []);

  // Initialize form with existing data (for editing)
  const initializeForm = useCallback((data: ITaskFormValues) => {
    setFormData(data);
    setInitialFormData(data);
    setErrors({});
    setHasInteracted(false);
  }, []);

  return {
    formData,
    errors,
    isFormValid,
    hasUnsavedChanges,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    resetForm,
    initializeForm,
    
    // Assignee management
    addAssignee,
    removeAssignee,
    toggleAssignee,
    
    // Subtask management
    addSubtask,
    updateSubtask,
    removeSubtask,
    reorderSubtasks,
  };
};
